import { memo, useState } from 'react'

import { Star } from '@icon-park/react'

import { BrandResponse, CreateBrandRequest } from '@shared/models'

import { Alert, Api } from '@/modules'

import { useFileUploader, useInput, useRouting } from '@/hooks'

import { Button, Checkbox, Flex, Form, HorizontalTagInput, Input, TagOption, Uploader } from '@/components'

import { ManualCard, Modal, UploaderViewer } from '@/components/derived'
import { Str } from '@/utils/strings'
import { useCompanyContext } from '../CompanyDetails.context'
import { LogoContainer } from '../CompanyDetails.styles'
import { getDescription } from '../utils'
import { BrandsTable } from './Brands.table'
import { EditBrandModal } from './edit-modal/EditBrand.component'

export const Brands = memo(Component)

function Component(): JSX.Element | null {
  const { companyID, changeQueryParam, currentPage } = useRouting()
  const { company, reloadCompany, reloadBrands, reloadAllBrands } = useCompanyContext()

  const { sendAlert } = Alert.Hook()

  const { value: name, binder: nameBinder, reset: resetName } = useInput()

  const [copyKeyMessages, setCopyKeyMessages] = useState<boolean>(false)
  const [copyPromptRules, setCopyPromptRules] = useState<boolean>(false)
  const [termsTags, setTermsTags] = useState<TagOption[]>([])

  const { value: systemLogo, bind: systemLogoBinder, reset: resetSystemLogo } = useFileUploader()
  const { value: reportLogo, bind: reportLogoBinder, reset: resetReportLogo } = useFileUploader()

  const { isRunning: isCreatingBrand, run: createBrand } = Api.Command<BrandResponse>(Api.Methods.POST, `/brands`)

  function onSubmit(): void {
    if (!companyID) {
      return
    }

    if (Str.from(name).isBlank) {
      return sendAlert({
        type: 'error',
        title: 'Campo obrigatório',
        message: 'Nome é obrigatório',
      })
    }

    if (termsTags.length === 0) {
      return sendAlert({
        type: 'error',
        title: 'Campo obrigatório',
        message: 'Termos é obrigatório',
      })
    }

    const termsString = termsTags
      .map((tag) => tag.text)
      .filter((text) => text.trim() !== '')
      .join(';')

    const input: CreateBrandRequest = {
      companyID,
      name,
      systemLogo,
      reportLogo,
      copyKeyMessages,
      copyPromptRules,
      termsString,
    }

    createBrand<typeof input>({ input, onSuccess })
  }

  function onSuccess() {
    sendAlert({
      type: 'success',
      title: 'Sucesso',
      message: 'Marca cadastrada com sucesso!',
    })
    resetName()
    setCopyKeyMessages(false)
    setCopyPromptRules(false)
    resetSystemLogo()
    resetReportLogo()
    setTermsTags([])
    reloadBrands(currentPage)
    reloadAllBrands()
  }

  if (!companyID || !company) return null

  return (
    <>
      <ManualCard
        icon={Star}
        healthCheck={company.healthCheck?.isBrandsChecked}
        title="Marcas da Empresa"
        tooltip="Registre as marcas a serem monitoradas. Informe o nome e anexe os logotipos colorido e em escala de cinza no formato .SVG (417x128px)."
        description={getDescription(company.counts?.brands)}
        linkName="Editar"
        linkAction={() =>
          changeQueryParam({
            param: 'modal',
            value: 'marcas',
            replaceAll: true,
          })
        }
      />
      <Modal
        controlKey="marcas"
        backToName={company.name}
        title="Marcas da Empresa"
        subtitle={
          <span>
            Preencha todos os campos <strong>obrigatórios (*)</strong> para concluir o cadastro da marca.
          </span>
        }
        icon={Star}
        beforeClose={() => {
          reloadCompany()
        }}>
        <Form onSubmit={onSubmit} controlParam={isCreatingBrand}>
          <Flex $type="column" $align="stretch" $gap={24} $fit>
            <Input label="Nome da Marca" placeholder="Marca" {...nameBinder} required />

            <Flex $type="column" $gap={12}>
              <Checkbox
                checked={copyKeyMessages}
                onChange={(e) => setCopyKeyMessages(e.target.checked)}
                label="Copiar mensagens-chave da marca principal"
              />
              <Checkbox
                checked={copyPromptRules}
                onChange={(e) => setCopyPromptRules(e.target.checked)}
                label="Copiar prompt rules da marca principal"
              />
            </Flex>

            <Flex $type="row" $justify="center" $align="center" $gap={24} $fit>
              <LogoContainer>
                <Uploader
                  label="Logo em cores (.svg 417px/128px) - Opcional"
                  {...systemLogoBinder}
                  onUpload={(output) => {
                    systemLogoBinder.onUpload(output)
                  }}
                  requiredExtensions={['svg']}
                />
                <UploaderViewer value={systemLogo} />
              </LogoContainer>
              <LogoContainer>
                <Uploader
                  label="Logo em cinza (.svg 417px/128px) - Opcional"
                  {...reportLogoBinder}
                  onUpload={(output) => {
                    reportLogoBinder.onUpload(output)
                  }}
                  requiredExtensions={['svg']}
                />
                <UploaderViewer value={reportLogo} />
              </LogoContainer>
            </Flex>
            <Flex $type="row" $justify="center" $align="center" $gap={24} $fit>
              <HorizontalTagInput
                label="Termos (tags)*"
                placeholder="Digite termos separados por ponto e vírgula (;)"
                maxCharacters={1024}
                onChange={setTermsTags}
                preSelectedTags={termsTags}
                showClearButton={true}
                showBooleanHelp={true}
                exactQuotes={true}
              />
            </Flex>
            <Flex $type="row" $justify="flex-end" $align="stretch" $gap={20} $fit>
              <Button.Root type="button" $variant="primary" onClick={() => onSubmit()} $loading={isCreatingBrand}>
                <Button.Text value="Adicionar" />
              </Button.Root>
            </Flex>
          </Flex>
        </Form>
        <BrandsTable />
      </Modal>
      <EditBrandModal />
    </>
  )
}
